import React, { FC, useEffect, useState, useRef } from 'react';
import styled from 'styled-components/macro';

import { ColorVariants } from 'theme';
import dayjs from 'services/dayjs';
import media from 'helpers/media';

const ClockIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30" fill="none">
        <circle cx="15" cy="15" r="14" strokeWidth="2" />
        <line
            x1="14.9286"
            y1="1.07153"
            x2="14.9286"
            y2="15.0001"
            strokeWidth="2"
        />
        <line
            x1="14.6357"
            y1="14.2929"
            x2="21.0643"
            y2="20.7215"
            strokeWidth="2"
        />
    </svg>
);

const Root = styled.span<{ $color: ColorVariants; $fontSize: string }>`
    display: inline-flex;
    flex-direction: row;
    align-items: center;

    color: ${({ theme, $color }) => theme.colors[$color]};
    font-size: ${({ $fontSize }) => $fontSize};
    margin-top: ${({ theme }) => theme.space.mbase};

    ${({ theme }) => media.toTablet`
        margin-top: ${theme.space.small};
    `}

    & > svg {
        width: ${({ $fontSize }) => $fontSize};
        margin-right: ${({ theme }) => theme.space.sbase};
        stroke: ${({ theme, $color }) => theme.colors[$color]};
    }

    & > span {
        transform: uppercase;
    }
`;

type CountdownProps = {
    from: string;
    to?: string;
    color?: ColorVariants;
    showClock?: boolean;
    fontSize?: string;
} & React.HTMLAttributes<HTMLDivElement>;

const getFormattedString = (diff: number) =>
    dayjs.duration(diff, 'seconds').format('DD:HH:mm:ss');

const Countdown: FC<CountdownProps> = ({
    color,
    from,
    to,
    showClock = true,
    fontSize = '2rem',
    ...rest
}) => {
    const fromDate = dayjs(from);
    const toDate = to ? dayjs(to) : dayjs();
    const difference = useRef(fromDate.diff(toDate, 'seconds'));
    const [secondsLeft, setSecondsLeft] = useState<number>(difference.current);

    useEffect(() => {
        const intervalId = setInterval(() => {
            setSecondsLeft((t) => {
                if (t > 1) return t - 1;

                clearInterval(intervalId);
                return 0;
            });
        }, 1000);

        return () => clearInterval(intervalId);
    }, []);

    return (
        <Root $color={color ? color : 'white'} $fontSize={fontSize} {...rest}>
            {showClock && <ClockIcon />}
            <span>{getFormattedString(secondsLeft)}</span>
        </Root>
    );
};

export default Countdown;
